import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, LOGO_URL } from "@/lib/api";
import { toast } from "sonner";
import GoogleSignInButton from "@/components/auth/GoogleSignInButton";

export default function Register() {
  const { register, siteSettings } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referral, setReferral] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) setReferral(ref);
  }, [params]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!termsAccepted) {
      setError("Please read and accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setBusy(true);
    try {
      // Preserve the origin website — the referrer of the very first visit to
      // this browser session, captured in Landing.jsx via useTrackVisit and
      // persisted in localStorage. Falls back to document.referrer.
      let landingReferrer = "";
      try {
        landingReferrer = localStorage.getItem("tex_first_referrer") || document.referrer || "";
      } catch { /* storage may be blocked */ }
      await register({ username, email, password, referral_code: referral || null, terms_accepted: true, landing_referrer: landingReferrer });
      toast.success("Welcome! Your account is ready.");
      navigate("/login-deal");
    } catch (err) {
      setError(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-blue-800 dark:bg-slate-950 flex flex-col" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <div className="p-4"><Link to="/"><img src={siteSettings?.logo_url || LOGO_URL} alt="logo" className="h-12" /></Link></div>
      <div className="flex-1 flex items-center justify-center px-4 py-6">
        <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl shadow-2xl p-8 border border-slate-200 dark:border-slate-800">
          <h1 className="text-3xl font-black text-slate-900 dark:text-white mb-1" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Create account</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">Get 50 free credits to start surfing.</p>
          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Username</label>
              <input data-testid="register-username-input" required minLength={3} value={username} onChange={(e) => setUsername(e.target.value)} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-3 outline-none focus:border-blue-500 text-slate-900 dark:text-slate-100" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Email</label>
              <input data-testid="register-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-3 outline-none focus:border-blue-500 text-slate-900 dark:text-slate-100" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Password</label>
              <input data-testid="register-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-3 outline-none focus:border-blue-500 text-slate-900 dark:text-slate-100" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Referral code (optional)</label>
              <input data-testid="register-ref-input" value={referral} onChange={(e) => setReferral(e.target.value)} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-3 outline-none focus:border-blue-500 text-slate-900 dark:text-slate-100" />
            </div>
            {error && <div data-testid="register-error" className="text-red-600 dark:text-red-400 text-sm">{error}</div>}

            <label
              className="flex items-start gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-3 cursor-pointer select-none"
              data-testid="register-terms-label"
            >
              <input
                data-testid="register-terms-checkbox"
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 dark:border-slate-600 accent-blue-600 cursor-pointer"
              />
              <span className="text-xs leading-relaxed text-slate-700 dark:text-slate-300">
                I have read and agree to the{" "}
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="register-terms-link"
                  className="text-blue-700 dark:text-blue-400 font-semibold hover:underline"
                >
                  Terms of Service and Privacy Policy
                </Link>
                .
              </span>
            </label>

            <button data-testid="register-submit-btn" disabled={busy || !termsAccepted} className="w-full rounded-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 disabled:cursor-not-allowed text-yellow-950 font-black py-3 transition-colors duration-200">
              {busy ? "Creating…" : "Create free account"}
            </button>
          </form>

          {siteSettings?.google_signin_enabled && siteSettings?.google_client_id && (
            <>
              <div className="flex items-center gap-3 my-4" data-testid="register-google-divider">
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">or</div>
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
              </div>
              <div className="flex justify-center">
                <GoogleSignInButton clientId={siteSettings.google_client_id} mode="signup_with" />
              </div>
            </>
          )}

          <div className="mt-4 text-sm text-slate-600 dark:text-slate-300">
            Already have an account? <Link data-testid="register-login-link" to="/login" className="text-blue-700 dark:text-blue-400 font-semibold hover:underline">Sign in</Link>
          </div>
        </div>
      </div>
      <div className="text-center pb-6 text-xs text-white/80">
        <Link data-testid="register-footer-terms-link" to="/terms" className="hover:underline">
          Terms of Service &amp; Privacy Policy
        </Link>
      </div>
    </div>
  );
}
